/**
 * Client-Side Encryption for Health Vault
 * 
 * IMPORTANT: All health data is encrypted on the user's device BEFORE
 * being sent to the server. The server NEVER sees unencrypted health data.
 * 
 * Flow:
 * 1. User sets a 6-digit Vault PIN
 * 2. PIN is used to derive an AES-256 encryption key (PBKDF2)
 * 3. All health data is encrypted with this key before upload
 * 4. Server stores only encrypted blobs
 * 5. Data is decrypted on device when user enters PIN
 */

// Convert string to ArrayBuffer
const stringToBuffer = (str) => {
  return new TextEncoder().encode(str);
};

// Convert ArrayBuffer to string
const bufferToString = (buffer) => {
  return new TextDecoder().decode(buffer);
};

// Convert ArrayBuffer to Base64
const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  return btoa(binary);
};

// Convert Base64 to ArrayBuffer
const base64ToBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Generate a random salt
const generateSalt = () => {
  return crypto.getRandomValues(new Uint8Array(16));
};

// Derive encryption key from PIN using PBKDF2
const deriveKeyFromPIN = async (pin, salt) => {
  const pinBuffer = stringToBuffer(pin);
  
  // Import PIN as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    pinBuffer,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );
  
  // Derive AES-256 key using PBKDF2 with 100,000 iterations
  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
  
  return key;
};

/**
 * Encrypt data with the vault key
 * @param {object|string} data - Data to encrypt
 * @param {CryptoKey} key - Derived encryption key
 * @returns {string} - Base64 encoded encrypted data with IV
 */
export const encryptData = async (data, key) => {
  try {
    const dataString = typeof data === 'string' ? data : JSON.stringify(data);
    const dataBuffer = stringToBuffer(dataString);
    
    // Generate random IV for each encryption
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // Encrypt
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      dataBuffer
    );
    
    // Combine IV + encrypted data
    const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedBuffer), iv.length);
    
    return bufferToBase64(combined.buffer);
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypt data with the vault key
 * @param {string} encryptedBase64 - Base64 encoded encrypted data
 * @param {CryptoKey} key - Derived encryption key
 * @returns {object|string} - Decrypted data
 */
export const decryptData = async (encryptedBase64, key) => {
  try {
    const combined = new Uint8Array(base64ToBuffer(encryptedBase64));
    
    // Extract IV (first 12 bytes) and encrypted data
    const iv = combined.slice(0, 12);
    const encryptedData = combined.slice(12);
    
    // Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      encryptedData
    );
    
    const decryptedString = bufferToString(decryptedBuffer);
    
    // Try to parse as JSON, otherwise return string
    try {
      return JSON.parse(decryptedString);
    } catch {
      return decryptedString;
    }
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Failed to decrypt data - wrong PIN?');
  }
};

/**
 * Initialize vault with a new PIN
 * @param {string} pin - 6-digit PIN
 * @returns {object} - Salt (to be stored) and key (to be kept in memory)
 */
export const initializeVault = async (pin) => {
  if (!pin || pin.length !== 6 || !/^\d{6}$/.test(pin)) {
    throw new Error('PIN must be exactly 6 digits');
  }
  
  const salt = generateSalt();
  const key = await deriveKeyFromPIN(pin, salt);
  
  return {
    salt: bufferToBase64(salt.buffer),
    key: key
  };
};

/**
 * Unlock vault with existing PIN
 * @param {string} pin - 6-digit PIN
 * @param {string} saltBase64 - Stored salt in Base64
 * @returns {CryptoKey} - Derived encryption key
 */
export const unlockVault = async (pin, saltBase64) => {
  if (!pin || pin.length !== 6 || !/^\d{6}$/.test(pin)) {
    throw new Error('PIN must be exactly 6 digits');
  }
  
  const salt = new Uint8Array(base64ToBuffer(saltBase64));
  const key = await deriveKeyFromPIN(pin, salt);
  
  return key;
};

/**
 * Change vault PIN
 * @param {string} oldPin - Current PIN
 * @param {string} newPin - New PIN
 * @param {string} oldSaltBase64 - Current salt
 * @param {Array} encryptedRecords - All encrypted records to re-encrypt
 * @returns {object} - New salt and re-encrypted records
 */
export const changeVaultPIN = async (oldPin, newPin, oldSaltBase64, encryptedRecords) => {
  // Unlock with old PIN
  const oldKey = await unlockVault(oldPin, oldSaltBase64);
  
  // Create new vault with new PIN
  const { salt: newSalt, key: newKey } = await initializeVault(newPin);
  
  // Re-encrypt all records
  const reEncryptedRecords = await Promise.all(
    encryptedRecords.map(async (record) => {
      if (record.encrypted_blob) {
        const decrypted = await decryptData(record.encrypted_blob, oldKey);
        const reEncrypted = await encryptData(decrypted, newKey);
        return { ...record, encrypted_blob: reEncrypted };
      }
      return record;
    })
  );
  
  return {
    newSalt,
    newKey,
    reEncryptedRecords
  };
};

/**
 * Verify PIN is correct by attempting to decrypt a test value
 * @param {string} pin - PIN to verify
 * @param {string} saltBase64 - Stored salt
 * @param {string} testEncrypted - A test encrypted value
 * @returns {boolean} - True if PIN is correct
 */
export const verifyPIN = async (pin, saltBase64, testEncrypted) => {
  try {
    const key = await unlockVault(pin, saltBase64);
    await decryptData(testEncrypted, key);
    return true;
  } catch {
    return false;
  }
};

// Store vault key in memory (cleared on page refresh/close)
let vaultKeyInMemory = null;

export const setVaultKey = (key) => {
  vaultKeyInMemory = key;
};

export const getVaultKey = () => {
  return vaultKeyInMemory;
};

export const clearVaultKey = () => {
  vaultKeyInMemory = null;
};

export const isVaultUnlocked = () => {
  return vaultKeyInMemory !== null;
};
